/**
 * Engine de execucao de formulas de calculo dinamicas.
 * Implementa Shunting-yard algorithm (Dijkstra) para parser de expressoes.
 * Espelhado do FormulaCalculoService.php (verso-api).
 */

const PRECEDENCIA = { "+": 1, "-": 1, "*": 2, "/": 2 };
const OPERADORES = ["+", "-", "*", "/"];

/**
 * Tokeniza uma expressao matematica em tokens tipados.
 * @param {string} expressao
 * @returns {Array|null} null se invalida
 */
function tokenizar(expressao) {
  const tokens = [];
  let pos = 0;
  const len = expressao.length;
  let ultimoTipo = null;

  while (pos < len) {
    const char = expressao[pos];

    if (/\s/.test(char)) {
      pos++;
      continue;
    }

    if (char === "(") {
      tokens.push({ tipo: "abre_parentese", valor: "(" });
      ultimoTipo = "abre_parentese";
      pos++;
      continue;
    }

    if (char === ")") {
      tokens.push({ tipo: "fecha_parentese", valor: ")" });
      ultimoTipo = "fecha_parentese";
      pos++;
      continue;
    }

    if (OPERADORES.includes(char)) {
      // Tratar numeros negativos
      if (
        char === "-" &&
        (ultimoTipo === null ||
          ultimoTipo === "operador" ||
          ultimoTipo === "abre_parentese")
      ) {
        let numStr = "-";
        pos++;
        while (pos < len && (/\d/.test(expressao[pos]) || expressao[pos] === ".")) {
          numStr += expressao[pos];
          pos++;
        }
        if (numStr === "-") return null;
        tokens.push({ tipo: "numero", valor: parseFloat(numStr) });
        ultimoTipo = "numero";
        continue;
      }

      tokens.push({ tipo: "operador", valor: char });
      ultimoTipo = "operador";
      pos++;
      continue;
    }

    if (/\d/.test(char) || char === ".") {
      let numStr = "";
      while (pos < len && (/\d/.test(expressao[pos]) || expressao[pos] === ".")) {
        numStr += expressao[pos];
        pos++;
      }
      tokens.push({ tipo: "numero", valor: parseFloat(numStr) });
      ultimoTipo = "numero";
      continue;
    }

    if (/[a-zA-Z_]/.test(char)) {
      let varStr = "";
      while (pos < len && /[a-zA-Z0-9_]/.test(expressao[pos])) {
        varStr += expressao[pos];
        pos++;
      }
      tokens.push({ tipo: "variavel", valor: varStr });
      ultimoTipo = "variavel";
      continue;
    }

    return null;
  }

  return tokens.length > 0 ? tokens : null;
}

/**
 * Avalia uma expressao matematica usando Shunting-yard.
 * @param {string} expressao
 * @param {Object} contexto - mapa variavel => valor numerico
 * @returns {number|null} null em caso de erro
 */
export function avaliarExpressao(expressao, contexto) {
  const tokens = tokenizar(expressao);
  if (!tokens) return null;

  // Shunting-yard: converter para RPN
  const saida = [];
  const operadores = [];

  for (const token of tokens) {
    switch (token.tipo) {
      case "numero":
        saida.push(token.valor);
        break;

      case "variavel":
        if (!(token.valor in contexto)) return null;
        saida.push(+contexto[token.valor]);
        break;

      case "operador":
        while (
          operadores.length > 0 &&
          operadores[operadores.length - 1] !== "(" &&
          (PRECEDENCIA[operadores[operadores.length - 1]] || 0) >=
            (PRECEDENCIA[token.valor] || 0)
        ) {
          saida.push(operadores.pop());
        }
        operadores.push(token.valor);
        break;

      case "abre_parentese":
        operadores.push("(");
        break;

      case "fecha_parentese":
        while (
          operadores.length > 0 &&
          operadores[operadores.length - 1] !== "("
        ) {
          saida.push(operadores.pop());
        }
        if (operadores.length === 0) return null;
        operadores.pop();
        break;
    }
  }

  while (operadores.length > 0) {
    const op = operadores.pop();
    if (op === "(") return null;
    saida.push(op);
  }

  // Avaliar RPN
  const pilha = [];
  for (const item of saida) {
    if (typeof item === "number") {
      pilha.push(item);
    } else if (OPERADORES.includes(item)) {
      if (pilha.length < 2) return null;
      const b = pilha.pop();
      const a = pilha.pop();
      let resultado;
      switch (item) {
        case "+": resultado = a + b; break;
        case "-": resultado = a - b; break;
        case "*": resultado = a * b; break;
        case "/": resultado = b !== 0 ? a / b : 0; break;
      }
      if (resultado === null) return null;
      pilha.push(resultado);
    } else {
      return null;
    }
  }

  return pilha.length === 1 ? pilha[0] : null;
}

/**
 * Executa uma formula completa (array de linhas) com valores fornecidos.
 * @param {Array} linhas - [{ordem, resultado, expressao}, ...]
 * @param {Object} valores - mapa variavel canonica => valor numerico
 * @param {string} resultadoFinal - nome da variavel de resultado final
 * @returns {Object} { erro, resultado, intermediarios, linhaErro }
 */
export function executarFormula(linhas, valores, resultadoFinal) {
  const contexto = { ...valores };
  const intermediarios = {};

  for (const linha of linhas) {
    const resultado = avaliarExpressao(linha.expressao, contexto);

    if (resultado === null) {
      return {
        erro: true,
        resultado: null,
        intermediarios,
        linhaErro: linha.ordem,
      };
    }

    contexto[linha.resultado] = resultado;
    intermediarios[linha.resultado] = resultado;
  }

  return {
    erro: false,
    resultado: contexto[resultadoFinal] || 0,
    intermediarios,
    linhaErro: null,
  };
}

/**
 * Extrai as variaveis referenciadas em uma expressao.
 * @param {string} expressao
 * @returns {Array<string>}
 */
export function extrairVariaveis(expressao) {
  const tokens = tokenizar(expressao);
  if (!tokens) return [];
  return tokens
    .filter((t) => t.tipo === "variavel")
    .map((t) => t.valor);
}

/**
 * Valida se uma expressao e sintaticamente correta.
 * @param {string} expressao
 * @param {Array<string>} variaveisDisponiveis - nomes canonicos validos
 * @param {Array<string>} resultadosDisponiveis - nomes de resultados de linhas anteriores
 * @returns {Object} { valida, erro }
 */
export function validarExpressao(expressao, variaveisDisponiveis = [], resultadosDisponiveis = []) {
  if (!expressao || !expressao.trim()) {
    return { valida: false, erro: "Expressao vazia" };
  }

  const tokens = tokenizar(expressao);
  if (!tokens) {
    return { valida: false, erro: "Sintaxe invalida" };
  }

  // Checar parenteses
  let count = 0;
  for (const char of expressao) {
    if (char === "(") count++;
    if (char === ")") count--;
    if (count < 0) return { valida: false, erro: "Parenteses desbalanceados" };
  }
  if (count !== 0) return { valida: false, erro: "Parenteses desbalanceados" };

  // Checar variaveis
  const todosDisponiveis = [...variaveisDisponiveis, ...resultadosDisponiveis];
  for (const token of tokens) {
    if (token.tipo === "variavel" && !todosDisponiveis.includes(token.valor)) {
      return { valida: false, erro: `Variavel '${token.valor}' nao encontrada` };
    }
  }

  return { valida: true, erro: null };
}
